/**
 * Every React Query key the client uses, in one place.
 *
 * Keys are hierarchical: invalidating `queryKeys.jobs.all` refreshes every job
 * list, `queryKeys.site(id)` one site. A mutation hook names the keys its
 * change makes stale; nothing else invalidates by hand.
 */
export const queryKeys = {
  me: ['me'] as const,

  overview: ['overview'] as const,
  systemSettings: ['system-settings'] as const,

  jobs: {
    all: ['jobs'] as const,
    /** The dashboard's newest jobs. */
    recent: ['jobs', 'recent'] as const,
    /** The jobs screen, by state filter. */
    list: (state: string) => ['jobs', 'list', state] as const,
    /** A site's newest jobs, for what its page has to know now. */
    site: (siteId: string) => ['jobs', 'site', siteId] as const,
    /** A site's full history, paged. */
    siteHistory: (siteId: string) => ['jobs', 'site-history', siteId] as const,
  },

  nodes: {
    all: ['nodes'] as const,
    /** A server's own page (ADR-0058); under `nodes`, so whatever refreshes the list refreshes it. */
    detail: (nodeId: string) => ['nodes', nodeId, 'detail'] as const,
    remnants: (nodeId: string) => ['nodes', nodeId, 'remnants'] as const,
    install: (installId: string) => ['nodes', 'installs', installId] as const,
  },
  servers: ['servers'] as const,

  sites: {
    all: ['sites'] as const,
    list: (search: string, status: string, tier: string, tags: unknown) => ['sites', search, status, tier, tags] as const,
    /** The sites on one server (ADR-0058). */
    server: (serverId: string) => ['sites', 'server', serverId] as const,
  },
  /** The 2d2s site tag, which only a restart of the panel changes. */
  siteSync: ['site-sync'] as const,
  site: (siteId: string) => ['site', siteId] as const,
  siteBackups: (siteId: string) => ['site-backups', siteId] as const,
  siteActivity: (siteId: string) => ['site-activity', siteId] as const,
  siteCredentials: (siteId: string) => ['site-credentials', siteId] as const,
  /** A server's credentials (ADR-0058). */
  nodeCredentials: (nodeId: string) => ['node-credentials', nodeId] as const,
  /** A site's DNS records and Cloudflare settings, read from 2d2s when the tab is opened. */
  siteDns: (siteId: string) => ['site-dns', siteId] as const,
  siteNameServers: (siteId: string, domainId: string) => ['site-dns', siteId, 'name-servers', domainId] as const,
  siteRedirects: (siteId: string, domainId: string) => ['site-dns', siteId, 'redirects', domainId] as const,

  tags: ['tags'] as const,

  /** Bulk runs (ADR-0046): the recent ones, and one followed. */
  bulk: {
    all: ['bulk-runs'] as const,
    run: (id: string) => ['bulk-runs', id] as const,
  },

  /** SEO checks (ADR-0045): every site's, and one site's. */
  seo: {
    network: ['seo'] as const,
    site: (siteId: string) => ['seo', 'site', siteId] as const,
  },

  /** Keys of outside services (ADR-0048), and one's own page (ADR-0052). */
  integrations: ['integrations'] as const,
  integration: (kind: string) => ['integrations', kind] as const,

  /** Google Search Console (ADR-0049): every site's line, and one site's. */
  searchConsole: {
    network: ['search-console'] as const,
    site: (siteId: string) => ['search-console', 'site', siteId] as const,
  },

  /** SEO data from outside sources (ADR-0050). */
  seoMetrics: {
    network: ['seo-metrics'] as const,
    site: (siteId: string) => ['seo-metrics', 'site', siteId] as const,
    /** A site's figures over time (ADR-0054). */
    timeline: (siteId: string) => ['seo-metrics', 'timeline', siteId] as const,
  },

  /** Automatic content (ADR-0051): every site's, one site's, one article. */
  content: {
    all: ['content'] as const,
    overview: ['content', 'overview'] as const,
    site: (siteId: string) => ['content', 'site', siteId] as const,
    item: (id: string) => ['content', 'item', id] as const,
  },

  /** Buying domains (ADR-0039): the panel's purchases, and one as 2d2s has it now. */
  domainPurchases: {
    all: ['domain-purchases'] as const,
    one: (id: string) => ['domain-purchases', id] as const,
  },

  /** Site orders (ADR-0059): the strip of sites being made, and one followed. */
  siteOrders: {
    all: ['site-orders'] as const,
    one: (id: string) => ['site-orders', id] as const,
  },

  /** Every site's copies (the Backups page). */
  backups: ['backups'] as const,
  /** The Backups page by server (ADR-0056); under `backups`, so a change to any copy refreshes it. */
  backupsOverview: ['backups', 'overview'] as const,
  backupSites: (filter: { serverId: string | null; q: string; problems: boolean }) => ['backups', 'sites', filter] as const,

  templates: {
    all: ['templates'] as const,
    /** Versions a new site can be created from. */
    active: ['templates', 'active'] as const,
    /** The templates screen: every version, deprecated ones too. */
    list: ['templates', 'list'] as const,
    /** Templates by name, with their versions and sites (ADR-0047). */
    families: ['templates', 'families'] as const,
  },
  /** A new site's creation, step by step (ADR-0047). */
  siteSetup: (siteId: string) => ['site-setup', siteId] as const,
  siteStructure: (siteId: string) => ['site-structure', siteId] as const,
  freeDomains: ['free-domains'] as const,

  users: {
    all: ['users'] as const,
    list: (search: string) => ['users', search] as const,
  },

  audit: (family: string, result: string) => ['audit', family, result] as const,
  /** What the trail says about one thing: a server's journal (ADR-0058). */
  auditOf: (entityType: string, entityId: string) => ['audit', 'entity', entityType, entityId] as const,
};
